'use client';

import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { createLogger } from '@svenskaspelab/nova-core/log';
import { useEffect, useState } from 'react';
import { SkeletonWrapper } from '../../../ui/SkeletonWrapper/entry.js';
import { SWE_HOCKEY_WIDGET_TYPES } from '../../providers/swehockey/entry.js';
const logger = createLogger('poolbetting.sportinfo.swehockey');
const statisticsSkeletonConfig = [{
  row: true,
  children: [{
    numLines: 1,
    className: 'h-5 w-40'
  }, {
    numLines: 1,
    className: 'h-5 w-40'
  }],
  className: 'mb-4'
}, {
  numLines: 5,
  className: 'h-8 w-full mb-2'
}];
/**
 * Initializes a SweHockey Impleo widget on the client.
 * - Renders a target DOM element for the widget.
 * - Calls `Impleo.loadWidget` on mount.
 * - Calls `Impleo.removeWidget` on unmount.
 *
 * @param root0 Props object
 * @param root0.item Content object for the SweHockey widget
 * @param root0.eventProps Event properties including eventId, drawNumber, and providers
 * @returns A div that will contain the SweHockey widget.
 */
export function SwehockeyWidget({
  item,
  eventProps
}) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(undefined);
  const widgetKey = item.type;
  const type = SWE_HOCKEY_WIDGET_TYPES[widgetKey];
  const {
    drawNumber,
    eventId,
    providers,
    theme = 'light'
  } = eventProps;
  const sweHockeyId = providers?.SweHockey;
  const uniqueId = `${type}_${sweHockeyId}`;
  useEffect(() => {
    if (!window.Impleo?.loadWidget) {
      logger.warn('Impleo.loadWidget is not available on window');
      void Promise.resolve().then(() => {
        setIsLoading(false);
      });
      return undefined;
    }
    if (!sweHockeyId) {
      logger.warn('sweHockeyId is not available', {
        drawNumber,
        eventId
      });
      void Promise.resolve().then(() => {
        setIsLoading(false);
      });
      return undefined;
    }
    let isActive = true;
    window.Impleo.loadWidget(uniqueId, type, sweHockeyId, false).then(() => {
      if (isActive) {
        setIsLoading(false);
      }
      return '';
    }).catch(error => {
      if (isActive) {
        setIsLoading(false);
        setHasError(error instanceof Error ? error : new Error(`Error loading SweHockeyWidget: ${String(error)}`));
      }
    });
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return () => {
      isActive = false;
      try {
        // Container may already be unmounted (e.g. hasError), removeWidget must still run
        window.Impleo?.removeWidget(uniqueId);
      } catch (error_0) {
        logger.error('Error removing SweHockeyWidget', {
          error: error_0,
          uniqueId
        });
      }
    };
  }, [drawNumber, eventId, sweHockeyId, type, uniqueId]);
  if (hasError) {
    logger.error('Error loading SweHockeyWidget', {
      error: hasError,
      uniqueId
    });
    return undefined;
  }
  return _jsxs(_Fragment, {
    children: [isLoading && _jsx(SkeletonWrapper, {
      config: statisticsSkeletonConfig
    }), _jsx("div", {
      className: `impleo-theme-${theme} impleo-align-with-page`,
      id: uniqueId
    })]
  });
}